import React from 'react'
import type { HTMLXStyledProps } from '@mote/ui/system'
import { forwardRef } from '@mote/ui/system'
import type { SystemProps } from '@xstyled/styled-components'
import { x } from '@xstyled/styled-components'
import type { MoteLogoWrapperProps } from './MoteLogo.styles'
import { MoteLogoWrapper } from './MoteLogo.styles'

export interface MoteLogoProps
  extends MoteLogoWrapperProps,
    Omit<HTMLXStyledProps<'svg'>, keyof MoteLogoWrapperProps | 'fill'> {
  fill?: string & SystemProps['fill']
  size?: number
  variant?: 'normal' | 'gradient'
}

export const MoteLogo = forwardRef<MoteLogoProps, 'svg'>((props, ref) => {
  const {
    color = 'white',
    fill = 'primary.base',
    size = 24,
    variant = 'normal',
    scale,
    ...rest
  } = props

  return (
    <MoteLogoWrapper
      ref={ref}
      viewBox={variant === 'normal' ? '0 0 24 24' : '0 0 100 100'}
      xmlns='http://www.w3.org/2000/svg'
      width={size}
      height={size}
      fill='none'
      role='presentation'
      aria-hidden={true}
      focusable={false}
      {...rest}
      color={color}
      scale={rest.isAnimated ? scale : undefined}
    >
      {variant === 'normal' && <NormalVariant fill={fill} />}
      {variant === 'gradient' && <GradientVariant fill={fill} stroke={fill} />}
    </MoteLogoWrapper>
  )
})

interface NormalVariantProps {
  fill: string & SystemProps['fill']
}

const NormalVariant = (props: NormalVariantProps) => {
  return (
    <>
      <x.circle cx='12' cy='12' r='12' {...props} />
      <path
        fill='currentcolor'
        d='M19.937 13.38V9.199c0-.028 0-.056-.002-.083l-.003-.039-.003-.043-.006-.048-.004-.032-.009-.053-.005-.027a1.856 1.856 0 0 0-.011-.053l-.006-.027a1.478 1.478 0 0 0-.014-.05l-.008-.03-.015-.046a1.794 1.794 0 0 0-.01-.032l-.016-.041a1.527 1.527 0 0 0-.03-.072l-.018-.04-.015-.03a1.45 1.45 0 0 0-.022-.044l-.014-.026a1.762 1.762 0 0 0-.027-.046l-.014-.023a1.523 1.523 0 0 0-.03-.046c-.004-.008-.01-.015-.015-.023l-.03-.043-.02-.025a1.49 1.49 0 0 0-.03-.039l-.028-.032-.025-.03a1.769 1.769 0 0 0-.117-.117l-.029-.025-.032-.028-.039-.03-.024-.02-.044-.031-.022-.016a1.67 1.67 0 0 0-.046-.03l-.023-.014-.045-.026-.026-.014-.043-.023-.03-.015-.04-.018-.036-.015-.036-.015a1.493 1.493 0 0 0-.041-.015l-.032-.011a1.482 1.482 0 0 0-.046-.015l-.028-.008a1.843 1.843 0 0 0-.051-.014l-.026-.006a1.502 1.502 0 0 0-.053-.012l-.028-.005a2.059 2.059 0 0 0-.052-.009l-.032-.004-.048-.005-.043-.004-.038-.003a1.655 1.655 0 0 0-.165 0l-.038.003-.043.004-.048.005-.032.004-.052.01c-.009 0-.018.002-.027.004a1.497 1.497 0 0 0-.053.012l-.027.006a1.745 1.745 0 0 0-.05.014l-.028.008c-.016.004-.031.01-.047.015l-.032.01-.04.016-.037.015c-.012.004-.024.01-.035.015l-.04.018-.03.015-.044.023-.026.014-.045.026-.023.015-.046.03-.022.015-.044.03-.024.02-.04.031-.031.028a1.026 1.026 0 0 0-.088.081l-.001.001-3.813 3.835V4.6a1.68 1.68 0 0 0-1.027-1.545 1.655 1.655 0 0 0-1.813.363L5.457 8.434A1.668 1.668 0 0 0 4.97 9.62v1.67l-3.255 3.274a1.662 1.662 0 0 0 1.813.362c.201-.084.385-.207.54-.362l4.228-4.253v7.251a1.68 1.68 0 0 0 1.027 1.545 1.654 1.654 0 0 0 1.812-.362l3.814-3.835v3.488c0 .444.175.87.487 1.183.312.314.735.49 1.176.49l4.989-5.018c-.441 0-.864-.176-1.176-.49a1.677 1.677 0 0 1-.487-1.182Z'
      />
    </>
  )
}

interface GradientVariantProps {
  fill: string & SystemProps['fill']
  stroke: string & SystemProps['stroke']
}

const GradientVariant = (props: GradientVariantProps) => {
  return (
    <>
      <x.circle cx='50' cy='50' r='49.102' {...props} strokeWidth='1.796' />
      <circle cx='50' cy='50' r='47.5' fill='url(#a)' opacity='.21' />
      <g opacity='.1'>
        <circle cx='50' cy='50' r='47.5' fill='url(#b)' opacity='.21' />
      </g>
      <path
        fill='currentColor'
        d='M78.832 54.848v-14.94c0-.098-.002-.197-.007-.295l-.01-.138c-.004-.05-.007-.102-.013-.153-.006-.059-.014-.117-.021-.175l-.015-.113a5.668 5.668 0 0 0-.033-.188c-.006-.033-.011-.066-.018-.098a6.198 6.198 0 0 0-.043-.19l-.022-.095a6.206 6.206 0 0 0-.051-.182l-.03-.101c-.017-.056-.036-.11-.056-.167-.013-.038-.025-.076-.04-.114-.018-.05-.037-.1-.057-.149-.017-.043-.034-.086-.053-.128a6.55 6.55 0 0 0-.057-.129l-.067-.143-.055-.108a6.132 6.132 0 0 0-.083-.157l-.053-.092a6.919 6.919 0 0 0-.097-.163l-.053-.083a5.814 5.814 0 0 0-.108-.163l-.059-.082a5.6 5.6 0 0 0-.114-.155l-.072-.09c-.037-.046-.074-.093-.112-.138-.034-.04-.069-.078-.103-.116l-.094-.105a6.354 6.354 0 0 0-.431-.418c-.036-.031-.073-.06-.108-.091l-.12-.1a4.91 4.91 0 0 0-.144-.108l-.091-.07a5.084 5.084 0 0 0-.16-.11l-.084-.057a6.062 6.062 0 0 0-.168-.105l-.086-.052-.168-.094-.096-.051c-.053-.028-.107-.054-.16-.08l-.112-.054a6.04 6.04 0 0 0-.148-.065l-.132-.055-.133-.051a5.743 5.743 0 0 0-.153-.056l-.118-.039a5.48 5.48 0 0 0-.172-.053l-.105-.03a6.58 6.58 0 0 0-.188-.048l-.098-.023a5.71 5.71 0 0 0-.196-.041l-.101-.018a7.628 7.628 0 0 0-.194-.031l-.117-.015c-.06-.007-.12-.015-.18-.02-.053-.006-.106-.009-.159-.012-.047-.004-.094-.008-.141-.01a6.342 6.342 0 0 0-.61 0 3.27 3.27 0 0 0-.142.01c-.053.003-.106.006-.16.011l-.18.021-.116.015c-.065.009-.13.02-.194.031l-.102.018c-.065.012-.13.027-.195.041l-.098.023a6.634 6.634 0 0 0-.188.049c-.035.01-.07.018-.105.029-.058.016-.114.035-.172.053l-.118.039-.153.056-.133.05-.132.056-.148.065-.112.054a5.508 5.508 0 0 0-.161.08c-.032.016-.064.034-.095.051-.057.03-.113.062-.169.094l-.085.052a4.324 4.324 0 0 0-.17.105l-.082.056a6.552 6.552 0 0 0-.162.11l-.091.07a2.913 2.913 0 0 0-.145.11c-.04.032-.078.066-.118.099a6.117 6.117 0 0 0-.327.291l-.004.004-14.142 13.701v-25.91a5.844 5.844 0 0 0-1.04-3.32 6.13 6.13 0 0 0-2.768-2.2 6.354 6.354 0 0 0-3.563-.34c-1.197.23-2.296.8-3.158 1.635L25.13 37.177a5.947 5.947 0 0 0-1.341 1.94 5.796 5.796 0 0 0-.469 2.291v5.97L11.25 59.073a6.184 6.184 0 0 0 2.001 1.295 6.34 6.34 0 0 0 4.72 0c.749-.3 1.429-.74 2.002-1.295l15.684-15.195v25.909c0 1.182.362 2.337 1.04 3.32a6.13 6.13 0 0 0 2.767 2.2 6.352 6.352 0 0 0 3.564.34 6.232 6.232 0 0 0 3.158-1.635l14.142-13.701v12.463c0 1.585.65 3.105 1.807 4.226a6.27 6.27 0 0 0 4.361 1.75L85 60.823a6.27 6.27 0 0 1-4.361-1.75 5.882 5.882 0 0 1-1.807-4.225z'
      />
      <defs>
        <linearGradient
          id='a'
          x1='97.5'
          x2='2.5'
          y1='2.5'
          y2='97.5'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#fff' stopOpacity='0' />
          <stop offset='.464' stopColor='#fff' />
          <stop offset='1' stopColor='#fff' stopOpacity='0' />
        </linearGradient>
        <linearGradient
          id='b'
          x1='97.5'
          x2='2.5'
          y1='2.5'
          y2='97.5'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#fff' stopOpacity='0' />
          <stop offset='.464' stopColor='#fff' />
          <stop offset='1' stopColor='#fff' stopOpacity='0' />
        </linearGradient>
      </defs>
    </>
  )
}
